import { cn } from '@/lib/utils'
import { AlertTriangle, Info, X } from 'lucide-react'
import { ReactNode } from 'react'
import { toast as sonnerToast } from 'sonner'

interface ToastProps {
  title?: string
  message?: string
  type?: 'success' | 'error' | 'info' | 'warning'
  icon?: ReactNode
  className?: string
}

// Default icons and background colors for toast types
const DEFAULT_SETTINGS = {
  success: {
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 1.5C14.7848 1.5 17.4555 2.60625 19.4246 4.57538C21.3938 6.54451 22.5 9.21523 22.5 12C22.5 14.7848 21.3938 17.4555 19.4246 19.4246C17.4555 21.3938 14.7848 22.5 12 22.5C9.21523 22.5 6.54451 21.3938 4.57538 19.4246C2.60625 17.4555 1.5 14.7848 1.5 12C1.5 9.21523 2.60625 6.54451 4.57538 4.57538C6.54451 2.60625 9.21523 1.5 12 1.5ZM10.692 14.0715L8.3595 11.7375C8.27588 11.6539 8.17661 11.5875 8.06736 11.5423C7.9581 11.497 7.84101 11.4737 7.72275 11.4737C7.60449 11.4737 7.4874 11.497 7.37814 11.5423C7.26889 11.5875 7.16962 11.6539 7.086 11.7375C6.91712 11.9064 6.82225 12.1354 6.82225 12.3743C6.82225 12.6131 6.91712 12.8421 7.086 13.011L10.056 15.981C10.1394 16.065 10.2386 16.1317 10.3479 16.1773C10.4571 16.2228 10.5744 16.2462 10.6927 16.2462C10.8111 16.2462 10.9284 16.2228 11.0376 16.1773C11.1469 16.1317 11.2461 16.065 11.3295 15.981L17.4795 9.8295C17.5642 9.74623 17.6316 9.647 17.6778 9.53755C17.724 9.42809 17.7481 9.31057 17.7487 9.19177C17.7492 9.07297 17.7262 8.95523 17.6811 8.84535C17.6359 8.73547 17.5694 8.63562 17.4854 8.55156C17.4015 8.46751 17.3017 8.4009 17.1919 8.3556C17.0821 8.31029 16.9644 8.28718 16.8455 8.28759C16.7267 8.288 16.6092 8.31193 16.4997 8.358C16.3902 8.40407 16.2909 8.47136 16.2075 8.556L10.692 14.0715Z"
          fill="#00AC9C"
        />
      </svg>
    ),
    className: 'bg-primary/20',
  },
  error: {
    icon: <X className="text-[#EF3826]" />,
    className: ' bg-[#EF3826]/20',
  },
  info: {
    icon: <Info className="text-primary" />,
    className: 'bg-primary/20 ',
  },
  warning: {
    icon: <AlertTriangle className="text-[#FFA756]" />,
    className: '# bg-[#FFA756] bg-opacity-20',
  },
}

function customToastCore({
  title,
  message,
  type = 'success',
  icon,
  className,
}: ToastProps) {
  const settings = {
    icon: icon || DEFAULT_SETTINGS[type].icon,
    className: className || DEFAULT_SETTINGS[type].className,
  }
  sonnerToast.custom((t) => (
    <div className="flex w-full max-w-md overflow-hidden rounded-lg bg-white shadow-lg md:min-w-[372px]">
      <div
        className={cn(
          'flex w-12 items-center justify-center',
          settings.className
        )}
      >
        {settings.icon}
      </div>
      <div className="flex flex-row w-full">
        <div className="flex-1 p-4 flex flex-col item-start justify-center">
          {title && <h3 className="text-sm font-semibold">{title}</h3>}
          {message && <p className="text-sm">{message}</p>}
        </div>
        <div
          className="py-4 px-4 cursor-pointer"
          onClick={() => sonnerToast.dismiss(t)}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18.295 7.115C18.6844 6.72564 18.6844 6.09436 18.295 5.705C17.9056 5.31564 17.2744 5.31564 16.885 5.705L12 10.59L7.115 5.705C6.72564 5.31564 6.09436 5.31564 5.705 5.705C5.31564 6.09436 5.31564 6.72564 5.705 7.115L10.59 12L5.705 16.885C5.31564 17.2744 5.31564 17.9056 5.705 18.295C6.09436 18.6844 6.72564 18.6844 7.115 18.295L12 13.41L16.885 18.295C17.2744 18.6844 17.9056 18.6844 18.295 18.295C18.6844 17.9056 18.6844 17.2744 18.295 16.885L13.41 12L18.295 7.115Z"
              fill="black"
            />
          </svg>
        </div>
      </div>
    </div>
  ))
}
// Export shorthand methods
export const toast = {
  base: customToastCore,
  success: ({ title, message, icon, className }: ToastProps) =>
    customToastCore({
      title,
      message,
      type: 'success',
      icon,
      className,
    }),
  error: ({ title, message, icon, className }: ToastProps) =>
    customToastCore({
      title,
      message,
      type: 'error',
      icon,
      className,
    }),
  info: ({ title, message, icon, className }: ToastProps) =>
    customToastCore({
      title,
      message,
      type: 'info',
      icon,
      className,
    }),
  warning: ({ title, message, icon, className }: ToastProps) =>
    customToastCore({
      title,
      message,
      type: 'warning',
      icon,
      className,
    }),

  custom: ({
    renderLayout,
    duration,
  }: {
    renderLayout: (t: string | number) => ReactNode
    duration: number
  }) => {
    return sonnerToast.custom((toastId) => <>{renderLayout(toastId)}</>, {
      duration: duration,
    })
  },
}
